import type { WithId } from '@medplum/core';
import type { FamilyMemberHistory } from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatAge, formatDeceased, formatRelationship, summarizeConditions } from './familyMemberHistoryUtils';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type { PromotionStatus } from './RecordListItem';

export type FamilyMemberHistoryListProps = {
  familyMemberHistories: WithId<FamilyMemberHistory>[];
  onSelectFamilyMember?: (id: string) => void;
  selectedFamilyMemberId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const getTitle = (history: FamilyMemberHistory, t: TFunction<'records'>): string => {
  const relationship = formatRelationship(history.relationship, t);
  return history.name ? `${relationship} · ${history.name}` : relationship;
};

const getDescription = (history: FamilyMemberHistory, t: TFunction<'records'>): string => {
  const ageOrDeceased =
    formatDeceased(history, t) ?? formatAge(history.ageAge ?? history.ageRange ?? history.ageString, t);
  const conditions = summarizeConditions(history, t);
  return [ageOrDeceased, conditions].filter(Boolean).join(' · ');
};

export const FamilyMemberHistoryList = ({
  familyMemberHistories,
  onSelectFamilyMember,
  selectedFamilyMemberId,
  onPromotionClick,
  getPromotionStatus,
}: FamilyMemberHistoryListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2">
        {familyMemberHistories.length > 0 ? (
          familyMemberHistories.map((history) => (
            <RecordListItem
              key={history.id}
              title={getTitle(history, t)}
              description={getDescription(history, t) || undefined}
              onClick={onSelectFamilyMember ? (): void => onSelectFamilyMember(history.id) : undefined}
              selected={selectedFamilyMemberId === history.id}
              onPromotionClick={onPromotionClick ? (): void => onPromotionClick(history.id) : undefined}
              promotionStatus={getPromotionStatus?.(history.id)}
            />
          ))
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noFamilyHistoryFound')}</p>
        )}
      </div>
    </div>
  );
};
